import React, { useMemo, useState, useContext, useEffect } from "react";
import { Box, Button, Step, StepLabel, Stepper, Typography } from "@mui/material";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import Step1SalonTarih from "./Step1SalonTarih";
import Step2KadinKimlik from "./Step2KadinKimlik";
import Step3ErkekKimlik from "./Step3ErkekKimlik";
import Step4IkametBilgileri from "./Step4IkametBilgileri";
import saveEvlBeyan from "../../../../apis/OB/SaveApi/EvlendirmeBasvuruAPI";
import { ThemeContext } from "../../../../context/themeProviderContext";
import { Alert, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";

// form verilerinin tip tanımı
export type FormValues = {
	step1: {
		evlenmeTuruId: string;
		evlenmeYeriTuruId?: string;
		nikahSalonuId: string;
		tarih: string;
		saat: string;
	};
	step2: Record<string, unknown>; // kadın kimlik bilgileri
	step3: Record<string, unknown>; // erkek kimlik bilgileri
	step4: Record<string, unknown>; // ikamet bilgileri
};

const validationSchemas = {
	0: Yup.object().shape({
		step1: Yup.object().shape({
			evlenmeTuruId: Yup.string().required("Evlenme türü zorunludur"),
			evlenmeYeriTuruId: Yup.string().optional(),
			nikahSalonuId: Yup.string().required("Nikah salonu zorunludur"),
			tarih: Yup.string().required("Tarih seçimi zorunludur"),
			saat: Yup.string().required("Saat seçimi zorunludur"),
		})
	}),
	1: Yup.object().shape({
		step2: Yup.object().shape({
			uyrukKodu: Yup.string().required("Uyruk zorunludur"),
			tcNo: Yup.string().required("TC No zorunludur")
				.matches(/^[0-9]+$/, 'Sadece rakam girebilirsiniz')
				.length(11, 'TC Kimlik No 11 haneli olmalıdır'),
			adi: Yup.string().required("Ad zorunludur"),
			soyadi: Yup.string().required("Soyad zorunludur"),
			babaAdi: Yup.string().required("Baba adı zorunludur"),
			anneAdi: Yup.string().required("Anne adı zorunludur"),
			dogumIlKodu: Yup.string().required("Doğum il/ilçe zorunludur"),
			dogumTarihi: Yup.string()
				.nullable()
				.transform((value, originalValue) => (originalValue === null || originalValue === undefined ? "" : value))
				.required("Doğum tarihi zorunludur"),
			kayitliIlKodu: Yup.string().required("Kayıtlı il/ilçe zorunludur"),
			mahalle: Yup.string().required("Mahalle/köy zorunludur"),
			cilt: Yup.string().required("Nüfus cüzdan seri no zorunludur"),
			dini: Yup.string().required("Dini alanı zorunludur"),
			cuzdanVerilisTarihi: Yup.string()
				.nullable()
				.transform((value, originalValue) => (originalValue === null || originalValue === undefined ? "" : value))
				.required("Cüzdan veriliş tarihi zorunludur"),
			kayitNo: Yup.string().optional(),
			verilisNedeni: Yup.string().required("Veriliş nedeni zorunludur")
		})
	}),
	2: Yup.object().shape({
		step3: Yup.object().shape({
			uyrukKodu: Yup.string().required("Uyruk zorunludur"),
			tcNo: Yup.string().required("TC No zorunludur")
				.matches(/^[0-9]+$/, 'Sadece rakam girebilirsiniz')
				.length(11, 'TC Kimlik No 11 haneli olmalıdır'),
			adi: Yup.string().required("Ad zorunludur"),
			soyadi: Yup.string().required("Soyad zorunludur"),
			babaAdi: Yup.string().required("Baba adı zorunludur"),
			anneAdi: Yup.string().required("Anne adı zorunludur"),
			dogumIlKodu: Yup.string().required("Doğum il/ilçe zorunludur"),
			dogumTarihi: Yup.string()
				.nullable()
				.transform((value, originalValue) => (originalValue === null || originalValue === undefined ? "" : value))
				.required("Doğum tarihi zorunludur"),
			kayitliIlKodu: Yup.string().required("Kayıtlı il/ilçe zorunludur"),
			mahalle: Yup.string().required("Mahalle/köy zorunludur"),
			cilt: Yup.string().required("Nüfus cüzdan seri no zorunludur"),
			dini: Yup.string().required("Dini alanı zorunludur"),
			cuzdanVerilisTarihi: Yup.string()
				.nullable()
				.transform((value, originalValue) => (originalValue === null || originalValue === undefined ? "" : value))
				.required("Cüzdan veriliş tarihi zorunludur"),
			kayitNo: Yup.string().optional(),
			verilisNedeni: Yup.string().required("Veriliş nedeni zorunludur")
		}),
	}),
	3: Yup.object().shape({
		step4: Yup.object().shape({
			bayanBelediyeIcinde: Yup.boolean().optional(),
			bayanMahalleAdi: Yup.string().required("Kadın mahalle adı zorunludur"),
			bayanCaddeSokak: Yup.string().required("Kadın cadde/sokak zorunludur"),
			bayanKapiNo: Yup.string()
				.required("Kadın kapı no zorunludur")
				.matches(/^\d+$/, 'Sadece rakam girebilirsiniz')
				.test('max-value', 'Kapı no en fazla 9999 olabilir', value => {
					if (!value) return true;
					return Number(value) <= 9999;
				}),
			bayanDaireNo: Yup.string()
				.required("Kadın daire no zorunludur")
				.matches(/^\d+$/, 'Sadece rakam girebilirsiniz')
				.test('max-value', 'Daire no en fazla 9999 olabilir', value => {
					if (!value) return true;
					return Number(value) <= 9999;
				}),
			bayanEvlilikSayisi: Yup.string()
				.matches(/^\d*$/, 'Sadece rakam girebilirsiniz')
				.test('max-value', 'Evlilik sayısı en fazla 999 olabilir', value => {
					if (!value) return true;
					return Number(value) <= 999;
				}),
			bayanCocukSayisi: Yup.string()
				.matches(/^\d*$/, 'Sadece rakam girebilirsiniz')
				.test('max-value', 'Çocuk sayısı en fazla 999 olabilir', value => {
					if (!value) return true;
					return Number(value) <= 999;
				}),
			bayanBakimCocukSayisi: Yup.string()
				.matches(/^\d*$/, 'Sadece rakam girebilirsiniz')
				.test('max-value', 'Bakmakla yükümlü çocuk sayısı en fazla 999 olabilir', value => {
					if (!value) return true;
					return Number(value) <= 999;
				}),
			bayanCepTelefon: Yup.string()
				.required("Kadın cep telefonu zorunludur")
				.matches(/^\d{10}$/, "Kadın cep telefonu 10 haneli olmalıdır"),
			bayanTelefon: Yup.string()
				.matches(/^\d{10}$/, "Cep telefonu 10 haneli olmalıdır"),
			bayanIlKodu: Yup.string().when("bayanBelediyeIcinde", {
				is: false, // belediye dışında ise il/ilçe zorunlu
				then: Yup.string().required("İl/İlçe zorunludur"),
				otherwise: Yup.string().optional(),
			}),

			erkekBelediyeIcinde: Yup.boolean().optional(),
			erkekMahalleAdi: Yup.string().required("Erkek mahalle adı zorunludur"),
			erkekCaddeSokak: Yup.string().required("Erkek cadde/sokak zorunludur"),
			erkekKapiNo: Yup.string()
				.required("Erkek kapı no zorunludur")
				.matches(/^\d+$/, 'Sadece rakam girebilirsiniz')
				.test('max-value', 'Kapı no en fazla 9999 olabilir', value => {
					if (!value) return true;
					return Number(value) <= 9999;
				}),
			erkekDaireNo: Yup.string()
				.required("Erkek daire no zorunludur")
				.matches(/^\d+$/, 'Sadece rakam girebilirsiniz')
				.test('max-value', 'Daire no en fazla 9999 olabilir', value => {
					if (!value) return true;
					return Number(value) <= 9999;
				}),

			erkekEvlilikSayisi: Yup.string()
				.matches(/^\d*$/, 'Sadece rakam girebilirsiniz')
				.test('max-value', 'Evlilik sayısı en fazla 999 olabilir', value => {
					if (!value) return true;
					return Number(value) <= 999;
				}),

			erkekCocukSayisi: Yup.string()
				.matches(/^\d*$/, 'Sadece rakam girebilirsiniz')
				.test('max-value', 'Çocuk sayısı en fazla 999 olabilir', value => {
					if (!value) return true;
					return Number(value) <= 999;
				}),

			erkekBakimCocukSayisi: Yup.string()
				.matches(/^\d*$/, 'Sadece rakam girebilirsiniz')
				.test('max-value', 'Bakmakla yükümlü çocuk sayısı en fazla 999 olabilir', value => {
					if (!value) return true;
					return Number(value) <= 999;
				}),
			erkekCepTelefon: Yup.string()
				.required("Erkek cep telefonu zorunludur")
				.matches(/^\d{10}$/, "Erkek cep telefonu 10 haneli olmalıdır"),
			erkekTelefon: Yup.string()
				.matches(/^\d{10}$/, "Cep telefonu 10 haneli olmalıdır"),
			erkekIlKodu: Yup.string().when("erkekBelediyeIcinde", {
				is: false, // belediye dışında ise il/ilçe zorunlu
				then: Yup.string().required("İl/İlçe zorunludur"),
				otherwise: Yup.string().optional(),
			}),

			// evlilik sonrası
			evlilikSonrasiKapiNo: Yup.string()
				.matches(/^\d*$/, 'Sadece rakam girebilirsiniz')
				.test('max-value', 'Kapı no en fazla 9999 olabilir', value => {
					if (!value) return true;
					return Number(value) <= 9999;
				}),

			evlilikSonrasiDaireNo: Yup.string()
				.matches(/^\d*$/, 'Sadece rakam girebilirsiniz')
				.test('max-value', 'Daire no en fazla 9999 olabilir', value => {
					if (!value) return true;
					return Number(value) <= 9999;
				}),
			evlilikSonrasiEmail: Yup.string().email("Geçerli bir e-posta adresi giriniz").required("Email zorunludur"),
			evlilikSonrasiIlKodu: Yup.string().optional(),
			evlilikSonrasiMahalle: Yup.string().optional(),
			evlilikSonrasiCaddeSokak: Yup.string().optional(),
		})
	})
};

const steps = [
	"Salon ve Tarih",
	"Kadın Kimlik Bilgileri",
	"Erkek Kimlik Bilgileri",
	"İkamet Bilgileri"
];

const MainStepper: React.FC = () => {

	const [successMessage, setSuccessMessage] = useState<string | null>(null);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);

	useEffect(() => {
		if (!successMessage) return;
		const t = setTimeout(() => setSuccessMessage(null), 5000);
		return () => clearTimeout(t);
	}, [successMessage]);

	const { ilIlceKodu }: any = useContext(ThemeContext)

	// başlangıç değerleri
	const initialValues: FormValues = {
		step1: {
			evlenmeTuruId: "",
			evlenmeYeriTuruId: undefined,
			nikahSalonuId: "",
			tarih: "",
			saat: ""
		},
		step2: {
			uyrukKodu: "1",
			tcNo: "",
			dogumTarihi: "",
			adi: "",
			soyadi: "",
			babaAdi: "",
			anneAdi: "",
			dogumIlKodu: "",
			kayitliIlKodu: "",
			mahalle: "",
			cilt: "",
			sira: "",
			kutukSira: "",
			dini: "",
			cuzdanVerilisTarihi: "",
			kayitNo: "",
			verilisNedeni: ""
		},
		step3: {
			uyrukKodu: "1",
			tcNo: "",
			dogumTarihi: "",
			adi: "",
			soyadi: "",
			babaAdi: "",
			anneAdi: "",
			dogumIlKodu: "",
			kayitliIlKodu: "",
			mahalle: "",
			cilt: "",
			sira: "",
			kutukSira: "",
			dini: "",
			cuzdanVerilisTarihi: "",
			kayitNo: "",
			verilisNedeni: ""
		},
		step4: {
			bayanBelediyeIcinde: false,
			bayanMahalleAdi: "",
			bayanCaddeSokak: "",
			bayanSiteApartman: "",
			bayanKapiNo: "",
			bayanAltKapiNo: "",
			bayanDaireNo: "",
			bayanAltDaireNo: "",
			bayanIlKodu: ilIlceKodu,
			bayanCepKod: "+90",
			bayanCepTelefon: "",
			bayanTelefon: "",
			bayanEvlilikSayisi: "",
			bayanCocukSayisi: "",
			bayanBakimCocukSayisi: "",
			bayanSonBosanmaTarihi: "",
			bayanMeslek: "",
			bayanOgrenimDurumu: "",
			erkekBelediyeIcinde: false,
			erkekMahalleAdi: "",
			erkekCaddeSokak: "",
			erkekSiteApartman: "",
			erkekKapiNo: "",
			erkekAltKapiNo: "",
			erkekDaireNo: "",
			erkekAltDaireNo: "",
			erkekIlKodu: ilIlceKodu,
			erkekCepKod: "+90",
			erkekCepTelefon: "",
			erkekTelefon: "",
			erkekEvlilikSayisi: "",
			erkekCocukSayisi: "",
			erkekBakimCocukSayisi: "",
			erkekSonBosanmaTarihi: "",
			erkekMeslek: "",
			erkekOgrenimDurumu: "",
			evlilikSonrasiIlKodu: "",
			evlilikSonrasiMahalle: "",
			evlilikSonrasiCaddeSokak: "",
			evlilikSonrasiKapiNo: "",
			evlilikSonrasiAltKapiNo: "",
			evlilikSonrasiDaireNo: "",
			evlilikSonrasiAltDaireNo: "",
			evlilikSonrasiTelefon: "",
			evlilikSonrasiEmail: "",
		}
	};

	const [activeStep, setActiveStep] = useState(0);
	const isLast = activeStep === steps.length - 1;

	const currentValidationSchema = useMemo(() => {
		return validationSchemas[activeStep as 0] ?? undefined;
	}, [activeStep]);

	const toNumberSafe = (val?: any): number | undefined => {
		if (val === null || val === undefined || val === "") return undefined;
		const s = String(val).trim();
		if (!/^-?\d+$/.test(s)) return undefined; // sadece tam sayı kabul et
		const n = Number(s);
		return Number.isFinite(n) ? n : undefined;
	};

	const toStringIfNumber = (val?: any): string | undefined => {
		if (val === null || val === undefined || val === "") return undefined;
		const s = String(val).trim();
		if (s === "") return undefined;
		return s;
	};

	const mapFormToApiRequest = (formValues: FormValues) => {
		const { step1, step2, step3, step4 } = formValues;

		const toDate = (isoString?: string) => {
			if (!isoString) return undefined;

			// Eğer sadece tarih varsa (YYYY-MM-DD), UTC öğlen olarak parse et
			if (isoString.length === 10 && isoString.includes('-')) {
				return new Date(isoString + 'T12:00:00.000Z');
			}

			// Eğer ISO string ise ve timezone bilgisi varsa
			if (isoString.includes('T')) {
				// Sadece tarih kısmını al ve UTC öğlen yap
				const dateOnly = isoString.split('T')[0];
				return new Date(dateOnly + 'T12:00:00.000Z');
			}

			return new Date(isoString);
		};

		const apiReq: any = {
			// üst bilgiler (aynı)
			VEvlenmeTuru: step1?.evlenmeTuruId,
			VEvlenmeYeriTuru: step1?.evlenmeYeriTuruId,
			VEvlenmeYeri: step1?.nikahSalonuId,
			VEvlenmeTarihi: toDate(step1?.tarih),
			VEvlenmeSaati: step1?.saat,

			// KADIN (VK*)
			VKVatandaslikNo: toNumberSafe((step2 as any)?.tcNo),
			VKAdi: (step2 as any)?.adi,
			VKSoyadi: (step2 as any)?.soyadi,
			VKBabaAdi: (step2 as any)?.babaAdi,
			VKAnneAdi: (step2 as any)?.anneAdi,
			VKDogumYeri: (step2 as any)?.dogumIlKodu,
			VKDogumTarihi: toDate((step2 as any)?.dogumTarihi),
			VKNufusaKayitliYer: (step2 as any)?.kayitliIlKodu,
			VKNufusaKayitliMahal: (step2 as any)?.mahalle,
			VKCiltNo: (step2 as any)?.cilt,
			VKNufusSiraNo: (step2 as any)?.sira,
			VKAileSiraNo: (step2 as any)?.kutukSira,
			VKDini: (step2 as any)?.dini,
			VKNufusCuzdanVerildigiTar: toDate((step2 as any)?.cuzdanVerilisTarihi),
			VKNufusCuzdanKayitNo: (step2 as any)?.kayitNo,
			VKNufusCuzdanVerilisNeden: (step2 as any)?.verilisNedeni,

			// ERKEK (VE*)
			VEVatandaslikNo: toNumberSafe((step3 as any)?.tcNo),
			VEAdi: (step3 as any)?.adi,
			VESoyadi: (step3 as any)?.soyadi,
			VEBabaAdi: (step3 as any)?.babaAdi,
			VEAnneAdi: (step3 as any)?.anneAdi,
			VEDogumYeri: (step3 as any)?.dogumIlKodu,
			VEDogumTarihi: toDate((step3 as any)?.dogumTarihi),
			VENufusaKayitliYer: (step3 as any)?.kayitliIlKodu,
			VENufusaKayitliMahal: (step3 as any)?.mahalle,
			VECiltNo: (step3 as any)?.cilt,
			VENufusSiraNo: (step3 as any)?.sira,
			VEAileSiraNo: (step3 as any)?.kutukSira,
			VEDini: (step3 as any)?.dini,
			VENufusCuzdanVerildigiTar: toDate((step3 as any)?.cuzdanVerilisTarihi),
			VENufusCuzdanKayitNo: (step3 as any)?.kayitNo,
			VENufusCuzdanVerilisNeden: (step3 as any)?.verilisNedeni,
		};

		// --- STEP4: İKAMET (bay & erkek) ---
		apiReq.VKMahalleKodu = toNumberSafe((step4 as any)?.bayanMahalleKodu);
		apiReq.VKCaddeSokakKodu = toNumberSafe((step4 as any)?.bayanCaddeSokakKodu);
		apiReq.VKKapiNo = toNumberSafe((step4 as any)?.bayanKapiNo);
		apiReq.VKAltKapiNo = toStringIfNumber((step4 as any)?.bayanAltKapiNo) || undefined;
		apiReq.VKDaireNo = toNumberSafe((step4 as any)?.bayanDaireNo);
		apiReq.VKAltDaireNo = toStringIfNumber((step4 as any)?.bayanAltDaireNo) || undefined;
		apiReq.VKIlIlce = (step4 as any)?.bayanBelediyeIcinde ? undefined : (step4 as any)?.bayanIlKodu || undefined;
		apiReq.VKCepNo = toStringIfNumber((step4 as any)?.bayanCepTelefon) || undefined;
		apiReq.VKAlanKodu = (step4 as any)?.bayanCepKod || '+90';
		apiReq.VKTelefonNo = toStringIfNumber((step4 as any)?.bayanTelefon) || undefined;

		apiReq.VKKacDefaEvlenmisti = toNumberSafe((step4 as any)?.bayanEvlilikSayisi);
		apiReq.VKOncekiEvlilikCocukSayis = toNumberSafe((step4 as any)?.bayanCocukSayisi);
		apiReq.VKBakmaklaYukumluCocukSay = toNumberSafe((step4 as any)?.bayanBakimCocukSayisi);
		apiReq.VKBosanmaTarihi = toDate((step4 as any)?.bayanSonBosanmaTarihi);


		apiReq.VKMeslekKodu = toNumberSafe((step4 as any)?.bayanMeslek);
		apiReq.VKOgrenimDurumu = toNumberSafe((step4 as any)?.bayanOgrenimDurumu);

		// ERKEK
		apiReq.VEMahalleKodu = toNumberSafe((step4 as any)?.erkekMahalleKodu);
		apiReq.VECaddeSokakKodu = toNumberSafe((step4 as any)?.erkekCaddeSokakKodu);
		apiReq.VEKapiNo = toNumberSafe((step4 as any)?.erkekKapiNo);
		apiReq.VEAltKapiNo = toStringIfNumber((step4 as any)?.erkekAltKapiNo) || undefined;
		apiReq.VEDaireNo = toNumberSafe((step4 as any)?.erkekDaireNo);
		apiReq.VEAltDaireNo = toStringIfNumber((step4 as any)?.erkekAltDaireNo) || undefined;
		apiReq.VEIlIlce = (step4 as any)?.erkekBelediyeIcinde ? undefined : (step4 as any)?.erkekIlKodu || undefined;
		apiReq.VECepNo = toStringIfNumber((step4 as any)?.erkekCepTelefon) || undefined;
		apiReq.VEAlanKodu = (step4 as any)?.erkekCepKod || '+90';
		apiReq.VETelefonNo = toStringIfNumber((step4 as any)?.erkekTelefon) || undefined;

		apiReq.VEKacDefaEvlenmisti = toNumberSafe((step4 as any)?.erkekEvlilikSayisi);
		apiReq.VEOncekiEvlilikCocukSayis = toNumberSafe((step4 as any)?.erkekCocukSayisi);
		apiReq.VEBakmaklaYukumluCocukSay = toNumberSafe((step4 as any)?.erkekBakimCocukSayisi);
		apiReq.VEBosanmaTarihi = toDate((step4 as any)?.erkekSonBosanmaTarihi);

		apiReq.VEMeslekKodu = toNumberSafe((step4 as any)?.erkekMeslek);
		apiReq.VEOgrenimDurumu = toNumberSafe((step4 as any)?.erkekOgrenimDurumu);

		// EVLİLİK SONRASI
		apiReq.VMIlIlceKodu = (step4 as any)?.evlilikSonrasiIlKodu || undefined;
		apiReq.VMMahalleAdi = (step4 as any)?.evlilikSonrasiMahalle || undefined;
		apiReq.VMCadSokAdi = (step4 as any)?.evlilikSonrasiCaddeSokak || undefined;
		apiReq.VMDiskapiNo = toStringIfNumber((step4 as any)?.evlilikSonrasiKapiNo) || undefined;
		apiReq.VMIckapiNo = toStringIfNumber((step4 as any)?.evlilikSonrasiAltKapiNo) || undefined;
		apiReq.VMAltDaireNo = toStringIfNumber((step4 as any)?.evlilikSonrasiAltDaireNo) || undefined;
		apiReq.VMTelefonNo = toStringIfNumber((step4 as any)?.evlilikSonrasiTelefon) || undefined;
		apiReq.VEEmail = (step4 as any)?.evlilikSonrasiEmail || undefined;

		apiReq.VKayitTarihi = new Date();
		apiReq.VBasvuruTarihi = new Date();
		apiReq.VBasvuruYili = new Date().getFullYear();

		return apiReq;
	};


	return (
		<Formik
			initialValues={initialValues}
			validationSchema={currentValidationSchema}
			validateOnChange={false}
			validateOnBlur={false}
			onSubmit={async (values, { resetForm }) => {
				if (!isLast) {
					setActiveStep((s) => s + 1);
					return;
				}

				const apiRequest = mapFormToApiRequest(values);
				console.log("API request:", apiRequest);

				try {
					const result = await saveEvlBeyan.evlBeyanSave(apiRequest);
					console.log("Kaydedildi:", result);
					//setSuccessMessage("Başvuru kaydedildi");
					toast.success("Başvuru başarıyla kaydedildi!");
					setErrorMessage(null);
					// formu sıfırla ve stepper'ı ilk adıma getir
					resetForm();
					setActiveStep(0);
				} catch (err: any) {
					console.error("Hata detayı (raw):", err);

					const backendMessage = String(err?.response?.data?.Message ?? err?.message ?? "");

					const cleaned = backendMessage
						// ORA-20101: gibi kodların başını kaldır
						.replace(/ORA-\d+:\s*/g, "")
						// "konum "..."", varsa sil (çift tırnak içindeki isimlerle birlikte)
						.replace(/konum\s*"[^"]*"\s*,?/gi, "")
						// "satır 610" gibi parçaları sil
						.replace(/satır\s*\d+/gi, "")
						// tek başına kalan "konum" kelimelerini sil
						.replace(/\bkonum\b/gi, "")
						// varsa ORA-06512: gibi satırın geri kalanını temizle (aynı satır)
						.replace(/ORA-06512:[^\n]*/gi, "")
						// virgül / noktalama artıklarını boşlukla değiştir
						.replace(/[,;:]+/g, " ")
						// yeni satırları tek boşluğa indir
						.replace(/\r?\n+/g, " ")
						// birden fazla boşluğu tek boşluğa indir
						.replace(/\s+/g, " ")
						.trim();

					// Eğer temiz mesaj boşsa daha genel bir mesaj göster
					setErrorMessage(cleaned || "Kaydetme sırasında bir hata oluştu.");
				}

			}}
		>
			{({ validateForm }) => (
				<>
					{successMessage && (
						<Box mb={2}>
							<Alert
								severity="success"
								action={
									<IconButton size="small" onClick={() => setSuccessMessage(null)}>
										<CloseIcon fontSize="small" />
									</IconButton>
								}
							>
								{successMessage}
							</Alert>
						</Box>
					)}

					{errorMessage && (
						<Box mb={2}>
							<Alert
								severity="error"
								action={
									<IconButton size="small" onClick={() => setErrorMessage(null)}>
										<CloseIcon fontSize="small" />
									</IconButton>
								}
							>
								{errorMessage}
							</Alert>
						</Box>
					)}
					<Form noValidate>
						<Box mb={3}>
							<Stepper activeStep={activeStep} alternativeLabel>
								{steps.map((label) => (
									<Step key={label}>
										<StepLabel>{label}</StepLabel>
									</Step>
								))}
							</Stepper>
						</Box>

						<Box>
							{activeStep === 0 && <Step1SalonTarih />}
							{activeStep === 1 && <Step2KadinKimlik />}
							{activeStep === 2 && <Step3ErkekKimlik />}
							{activeStep === 3 && <Step4IkametBilgileri />}
						</Box>

						<Box display="flex" justifyContent={activeStep === 0 ? "flex-end" : "space-between"} mt={3}>
							{activeStep > 0 && (
								<Button
									variant="outlined"
									onClick={() => setActiveStep((s) => Math.max(0, s - 1))}
								>
									Geri
								</Button>
							)}

							<Button
								variant="contained"
								color="primary"
								type="submit"
							>
								{isLast ? "Bitir" : "İleri"}
							</Button>
						</Box>

						{activeStep === 0 ? (
							<Typography variant="caption" display="block" color="text.secondary" mt={1}>
								Gün seçiminde dolu günler pasiftir. Gün seçimi sonrası uygun saatlerden biri seçilmelidir.
							</Typography>
						) : null}
					</Form>
				</>
			)}
		</Formik>
	);
};

export default MainStepper;